import ModalContainer from '@/components/molecules/ModalContainer';
import { background, text } from '@/theme/colors/palettes';
import { TContentType } from '@/types/Content.types';
import { Box, HStack, Image, Text, Tooltip, VStack } from '@chakra-ui/react';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import { MAX_VIDEO_DURATION_SECONDS } from './VideoSourceField';

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  onSelect: (type: TContentType) => void;
  asPage?: boolean;
};

const OPTIONS: {
  type: TContentType;
  label: string;
  description: string;
  iconSrc: string;
  disabled?: boolean;
}[] = [
  {
    type: 'video',
    label: 'Reel',
    description: `Short reels up to ${MAX_VIDEO_DURATION_SECONDS} seconds.`,
    iconSrc: '/svg/reel-icon.svg',
  },
  {
    type: 'post',
    label: 'Post',
    description: 'Short (300 characters max) textual content.',
    iconSrc: '/svg/post-icon.svg',
  },
  {
    type: 'article',
    label: 'Article',
    description: 'More complex textual content with images.',
    iconSrc: '/svg/article-icon.svg',
  },
];

const ContentTypePicker = ({ isOpen = false, onClose = () => {}, onSelect, asPage = false }: Props) => {
  const choices = (
    <VStack
      align="stretch"
      gap="16px"
      paddingX={'32px'}
      paddingTop={'36px'}
      paddingBottom={'42px'}
      alignItems={'center'}
    >
      <Text
        variant={'loraMediumTitle'}
        color={text.darkBlue}
        fontSize={'30px'}
        textAlign={'center'}
        marginBottom={'40px'}
      >
        Choose content you want to create:
      </Text>

      {OPTIONS.map(({ type, label, description, iconSrc, disabled }) => {
        const card = (
          <HStack
            key={type}
            as="button"
            disabled={disabled}
            onClick={disabled ? undefined : () => onSelect(type)}
            maxWidth="460px"
            minWidth={'440px'}
            gap="18px"
            textAlign="left"
            padding="18px 22px"
            borderRadius="16px"
            backgroundColor="white"
            boxShadow="0px 4px 16px rgba(14, 0, 98, 0.06)"
            opacity={disabled ? 0.6 : 1}
            cursor={disabled ? 'not-allowed' : 'pointer'}
            transition="all 0.18s ease"
            _hover={disabled ? {} : { boxShadow: '0px 8px 22px rgba(14, 0, 98, 0.14)', transform: 'translateY(-1px)' }}
          >
            <Box flexShrink={0}>
              <Image src={iconSrc} alt={label} width="36px" height="36px" objectFit="contain" />
            </Box>
            <VStack align="flex-start" flex={1} gap="2px">
              <HStack gap="8px">
                <Text fontSize="18px" fontWeight={700} color="text.darkBlue">
                  {label}
                </Text>
                {disabled && (
                  <Text
                    fontSize="10px"
                    fontWeight={700}
                    color="text.mediumGray"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    border="1px solid"
                    borderColor="background.gray"
                    borderRadius="full"
                    paddingX="8px"
                    paddingY="1px"
                  >
                    Coming soon
                  </Text>
                )}
              </HStack>
              <Text fontSize="13px" color="text.mediumBlue">
                {description}
              </Text>
            </VStack>
            <Box color="text.darkBlue" flexShrink={0}>
              <ArrowRight size={22} strokeWidth={2} />
            </Box>
          </HStack>
        );

        return disabled ? (
          <Tooltip key={type} label="Coming soon" placement="top">
            {card}
          </Tooltip>
        ) : (
          card
        );
      })}
    </VStack>
  );

  return asPage ? (
    choices
  ) : (
    <ModalContainer isOpen={isOpen} onClose={onClose} width="720px" backgroundColor={background.lightBlue}>
      {choices}
    </ModalContainer>
  );
};

export default ContentTypePicker;
